/**
 * Tables.
 *
 * Wide content scrolls inside `TableContainer`, never on the page body. The
 * header sticks so column meaning survives a long scroll. `DataTable` wraps the
 * primitives with the loading, empty and error states every list needs, so no
 * page has to reinvent them.
 *
 * THE SHELL AROUND THE ROWS IS OPT IN, AND THAT IS WHY IT COULD BE ADDED AT
 * ALL. A toolbar inside the card, a frozen first column, a columns picker, row
 * selection, sortable headers, a row kebab and a pager footer are what a wide
 * table is supposed to look like here, and each one arrives through a prop that
 * defaults to off. Thirty-five call sites already render a `DataTable`, spread
 * over settings, playbooks and prospects, and not one of them had to be
 * touched. A table that opts into nothing keeps the markup shape it had: it
 * fits its card, and only the measured sizes and colours changed.
 *
 * Sizes are the measured ones: a 36px header row, 44px body rows, 24px of cell
 * padding either side, 12/18 500 header labels and 14/20 cells, the first cell
 * 500 in the text colour and the rest 400 muted.
 *
 * EVERY COLUMN CAN BE DRAGGED. The owner, 2026-10-19: "columns in gojib must be
 * adjustable". Each header cell carries the inbox's `ResizeHandle` on its right
 * edge, the widths are worked out by `useColumnWidths` and remembered per table
 * in this browser, and a double click on any handle puts the whole table back.
 * A table built by hand out of the primitives gets the same with the hook and
 * `ColumnResizeHandle`, which the campaign and playbook lists do.
 *
 * AND MOVED. The owner, 2026-10-15: copy Blockcircle's fully adjustable table,
 * resizable and reorderable. A header is picked up and dropped on another, and
 * lands on the side of it the pointer was on, the way Blockcircle's do. The
 * first column, a row's actions and the add column stay put. The order is
 * remembered beside the widths (`useColumnOrder`), the columns picker lists
 * the columns in it with a step left and right for the keyboard, and the
 * picker's reset and a double click on a handle put the order back with the
 * widths.
 */
import type { CSSProperties, MouseEvent, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../lib/cn'
import { formatNumber, formatPercent } from '../lib/format'
import { Badge } from './Badge'
import { IconButton } from './Button'
import { ColumnPicker } from './ColumnPicker'
import type { ColumnChoice } from './ColumnPicker'
import { browserStore, readHiddenColumnsOr, writeHiddenColumns } from './column-picker-model'
import { tableWidthsKey } from './column-width-model'
import { useColumnOrder } from './useColumnOrder'
import { IconArrowDown, IconArrowUp, IconChevronLeft, IconChevronRight, IconSwitchVertical } from './icons'
import { Checkbox, Select } from './Input'
import { RowKebab } from './Menu'
import type { MenuSection } from './Menu'
import { ResizeHandle } from './ResizeHandle'
import type { PaginationProps } from './Pagination'
import { PAGE_SIZES, pageSizeOptions } from './pagination-model'
import { SelectionBar } from './SelectionBar'
import { PercentChip } from './Stat'
import type { SelectionBarProps } from './SelectionBar'
import { headerCheckState, toggleAllVisible, toggleSelected } from './selection-model'
import { EmptyState, ErrorState } from './States'
import type { EmptyStateProps } from './States'
import { ariaSort, nextHeaderSort } from './table-url-model'
import type { TableSort } from './table-url-model'
import { useColumnWidths } from './useColumnWidths'
import type { ColumnWidthsApi } from './useColumnWidths'

export type { SortOrder, TableSort } from './table-url-model'
// Lives beside the rest of the pager arithmetic, re-exported where it always was.
export { pageSizeOptions } from './pagination-model'

/**
 * The classes that make a table fit its card: nothing scrolls sideways, and a
 * long value (an address, a profile link) breaks inside its cell. Shared with
 * the few settings tables drawn by hand, so every table fits the same way.
 */
export const FIT_TABLE_CLASS = 'w-full max-w-full overflow-x-hidden [&_td]:[overflow-wrap:anywhere] [&_th]:[overflow-wrap:anywhere]'

/**
 * The classes for a table that is allowed to be wider than its card.
 *
 * The scroller wraps the table and nothing else, so the scrollbar sits directly
 * under the last row rather than under the footer (measured). It only ever
 * comes with a frozen first column, because a table that scrolls sideways
 * without one loses the column that says which row is which.
 */
export const SCROLL_TABLE_CLASS = 'w-full max-w-full overflow-x-auto'

export function TableContainer({
  children,
  className,
  /** Caps the height and scrolls vertically, which is what makes the header stick. */
  maxHeight,
  /** Drops the border and background, for a table that already sits inside a card. */
  flush = false,
  /**
   * Lets the table be wider than the card and scroll sideways inside it. Off
   * by default: most tables here fit ("it should fit neatly in a table without
   * needing to scroll to the right").
   */
  scrollX = false,
}: {
  children: ReactNode
  className?: string
  maxHeight?: string
  flush?: boolean
  scrollX?: boolean
}) {
  return (
    <div
      className={cn(
        // A long value breaks inside its cell instead of widening the table,
        // which is what lets the columns shrink to the card.
        scrollX ? SCROLL_TABLE_CLASS : FIT_TABLE_CLASS,
        'overflow-y-auto',
        !flush && 'rounded-card border border-line bg-surface',
        className,
      )}
      style={maxHeight ? { maxHeight } : undefined}
    >
      {children}
    </div>
  )
}

export function Table({
  children,
  className,
  style,
  dense = false,
  caption,
}: {
  children: ReactNode
  className?: string
  /** A width a scrolling table with dragged columns sets on itself. */
  style?: CSSProperties
  dense?: boolean
  /** Describes the table for screen readers. Visually hidden. */
  caption?: string
}) {
  return (
    <table
      className={cn('w-full border-collapse text-left text-base tabular-nums', dense && 'text-sm', className)}
      style={style}
    >
      {caption ? <caption className="sr-only">{caption}</caption> : null}
      {children}
    </table>
  )
}

export function THead({ children, className, sticky = true }: { children: ReactNode; className?: string; sticky?: boolean }) {
  return (
    <thead
      className={cn(
        'bg-surface-muted text-xs font-medium text-ink-muted',
        sticky && 'sticky top-0 z-sticky',
        className,
      )}
    >
      {children}
    </thead>
  )
}

export function TBody({ children, className }: { children: ReactNode; className?: string }) {
  return <tbody className={cn('divide-y divide-line', className)}>{children}</tbody>
}

/**
 * A whole row that needs to be read differently from its neighbours before
 * anybody reads a cell: `danger` for a person nobody may contact, `warning`
 * for one that needs a look. The fill is the tone's soft step, opaque, so a
 * pinned first cell can wear the same fill without the cells under it showing
 * through. A selected row's fill wins over the tone, and hover still answers
 * on a row that opens something.
 */
export type RowTone = 'danger' | 'warning'

export const ROW_TONE_CLASS: Record<RowTone, string> = {
  danger: 'bg-danger-soft',
  warning: 'bg-warning-soft',
}

export interface TRProps {
  children: ReactNode
  className?: string
  /** The row's own fill. See `RowTone`. */
  tone?: RowTone
  /** Mouse convenience only. Keyboard users follow the link inside the row. */
  onClick?: (event: MouseEvent<HTMLTableRowElement>) => void
  onAuxClick?: (event: MouseEvent<HTMLTableRowElement>) => void
  selected?: boolean
  /**
   * Whether the row answers to the pointer. A row that opens nothing must not
   * look as if it does, so `DataTable` turns this off for rows with no link
   * and no click. Hand-built tables keep the hover they always had.
   */
  hoverable?: boolean
}

export function TR({ children, className, tone, onClick, onAuxClick, selected, hoverable = true }: TRProps) {
  return (
    <tr
      data-tone={tone}
      onClick={onClick}
      onAuxClick={onAuxClick}
      className={cn(
        'transition-colors',
        onClick && 'cursor-pointer',
        selected ? 'bg-surface-hover' : [tone && ROW_TONE_CLASS[tone], hoverable && 'hover:bg-surface-hover'],
        className,
      )}
    >
      {children}
    </tr>
  )
}

export interface THProps extends ThHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean
}

export function TH({ children, className, numeric, ...rest }: THProps) {
  return (
    <th
      scope="col"
      className={cn(
        // Salesforge's th: 36 tall, 16px either side, 4px above and below.
        'h-row-tight whitespace-nowrap border-b border-line px-4 py-1 font-medium',
        numeric && 'text-right',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  )
}

export interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean
  /**
   * `primary` is the cell that names the row, 500 in the text colour.
   * `secondary` is every other cell in a list, 400 and muted. Left out, the
   * cell is plain text colour, which is what hand-built tables always had.
   */
  tone?: 'primary' | 'secondary'
}

export function TD({ children, className, numeric, tone, ...rest }: TDProps) {
  return (
    <td
      className={cn(
        // Salesforge's td: 44 tall, 16px either side, 4px above and below, so a
        // 32px control in the cell still leaves the row at 44.
        'h-row-loose px-4 py-1 align-middle',
        tone === 'primary' ? 'font-medium text-ink' : tone === 'secondary' ? 'text-ink-muted' : 'text-ink',
        numeric && 'text-right tabular-nums',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  )
}

/**
 * A rate in a table: the count in the primary weight and its share in a
 * `PercentChip`, right aligned for a numeric column. A rate with nothing under
 * it shows the count alone, because 0% of nothing is not a rate.
 */
export function RateCell({ count, rate, base, digits = 1 }: { count: number; rate: number; base: number; digits?: number }) {
  return (
    <span className="inline-flex items-center justify-end gap-2">
      <span className="font-medium text-ink">{formatNumber(count)}</span>
      {base > 0 ? <PercentChip>{formatPercent(rate, { digits })}</PercentChip> : null}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Dragging a column
// ---------------------------------------------------------------------------

/**
 * The classes a header cell needs to carry a handle: a positioned box, so the
 * strip can lie over its right edge. A pinned header is `sticky`, which is
 * positioned already, so it leaves this out.
 */
export const RESIZABLE_HEAD_CLASS = 'relative'

/**
 * The classes around a header's words once the columns have fixed widths: a
 * label longer than its column ends in an ellipsis instead of running under
 * the next header. The padding and the matching negative margin leave room
 * for the focus ring on a sort button, which the overflow would otherwise cut.
 */
export const HEAD_LABEL_CLASS = '-m-1 block min-w-0 truncate p-1'

/**
 * The handle on a column's right edge, for a table built by hand out of the
 * primitives. Rendered inside the header cell, after its words, with
 * `RESIZABLE_HEAD_CLASS` on the cell. Draws nothing for a column without a
 * handle (the last one of a fitted table, or one not measured yet).
 */
export function ColumnResizeHandle({
  widths,
  index,
  label,
  onReset,
}: {
  widths: ColumnWidthsApi
  index: number
  label: string
  /** What a double click puts back, when it is more than the widths: `DataTable` adds the order. */
  onReset?: () => void
}) {
  const handle = widths.handle(index)
  if (handle === null) return null
  return <ResizeHandle label={`Resize the ${label} column`} {...handle} {...(onReset === undefined ? {} : { onReset })} />
}

export interface ResizableTHProps extends THProps {
  widths: ColumnWidthsApi
  /** Where the cell sits in the header row, counted from the left. */
  index: number
  /** The column's key, which its dragged width is kept under. */
  column: string
  /** The column's name for the handle, when the children are not plain words. */
  label?: string
}

/**
 * A header cell for a table built by hand, with its handle and its dragged
 * width. The children are the header's words. The width hint a class gives
 * (`w-28`) stays until somebody drags, and the dragged width wins after that.
 */
export function ResizableTH({ widths, index, column, label, children, className, style, ...rest }: ResizableTHProps) {
  return (
    <TH {...rest} className={cn(RESIZABLE_HEAD_CLASS, className)} style={widths.columnStyle(column) ?? style}>
      <span className={HEAD_LABEL_CLASS}>{children}</span>
      <ColumnResizeHandle widths={widths} index={index} label={label ?? (typeof children === 'string' ? children : column)} />
    </TH>
  )
}

// ---------------------------------------------------------------------------
// The frozen first column
// ---------------------------------------------------------------------------

/**
 * The right edge of the frozen column, as a shadow rather than as a border.
 *
 * `Table` is `border-collapse`, and a collapsed border belongs to the table's
 * border grid rather than to the cell, so it stays where the grid put it while
 * a sticky cell slides away from it. The edge would detach from the column the
 * moment anybody scrolled, which is the one thing this column exists to
 * survive. An inset shadow is painted by the cell itself and travels with it.
 *
 * The colour is the border token read straight out of the custom property,
 * because a shadow has no Tailwind colour utility to reach for. It is the same
 * value `border-line` resolves to and it follows the theme the same way.
 */
const FROZEN_EDGE = 'shadow-[inset_-1px_0_0_rgb(var(--c-border))]'

/**
 * What pins a body cell to the left edge while the rest of the row slides under it.
 *
 * THE BACKGROUND IS THE WHOLE JOB. A transparent sticky cell shows the cells
 * passing beneath it and the column turns into an unreadable smear, so the cell
 * carries its own opaque fill, and that fill follows the row: the same hover,
 * the same selected fill. A row that opens nothing has no hover, so neither
 * does its pinned cell.
 *
 * The tier is `sticky-col`, below `sticky`. The header row is sticky too, and
 * a body cell level with it would paint over the header on document order the
 * moment the two crossed.
 */
export function frozenCellClass(selected = false, hoverable = true, tone?: RowTone): string {
  return cn(
    'sticky left-0 z-sticky-col',
    FROZEN_EDGE,
    selected
      ? 'bg-surface-hover'
      : cn(tone === undefined ? 'bg-surface' : ROW_TONE_CLASS[tone], hoverable && 'group-hover:bg-surface-hover'),
  )
}

/** The same, for the header cell, which takes the header's own fill. */
export const FROZEN_HEAD_CLASS = cn('sticky left-0 z-sticky-col bg-surface-muted', FROZEN_EDGE)

// ---------------------------------------------------------------------------
// The words on the shell
// ---------------------------------------------------------------------------

/**
 * The plain-text count that sits at the left of the toolbar: "1 sequence".
 *
 * Plain text and not a badge, because it is a fact about the list rather than a
 * control, and because a number with its noun beside it says what a bare number
 * in a pill does not. Zero reads as "No sequences": "0 sequences" is the sort
 * of sentence software writes and people do not.
 */
export function countLabel(count: number, singular: string, plural = `${singular}s`): string {
  if (count === 0) return `No ${plural}`
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

/**
 * "Page 3 of 7", or "Page 3" when nobody has said how many there are.
 *
 * An unknown total is written without a denominator rather than with a guessed
 * one, which is the rule `pageSummary` already follows for the same reason: a
 * list that answers without a count would otherwise be told it has exactly as
 * many pages as have been walked so far.
 *
 * The count of pages is never allowed below the page being looked at. A total
 * counted a moment before the page was cut can disagree with it under a
 * concurrent delete, and of the two readings that produces, "Page 3 of 2" is
 * the one that is certainly wrong.
 */
export function pageLabel(pageIndex: number, pageSize: number, total: number | undefined): string {
  const page = Math.max(1, pageIndex + 1)
  if (total === undefined || pageSize <= 0) return `Page ${formatNumber(page)}`
  const pages = Math.max(1, page, Math.ceil(total / pageSize))
  return `Page ${formatNumber(page)} of ${formatNumber(pages)}`
}

/** "Total items: 12", the chip some tables show in place of the size picker. */
export function totalItemsLabel(total: number): string {
  return `Total items: ${formatNumber(total)}`
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

export interface Column<T> {
  /** Stable key, also used for the React key of the cell. */
  key: string
  header: ReactNode
  cell: (row: T, index: number) => ReactNode
  numeric?: boolean
  /** A CSS width, for columns that should not stretch. */
  width?: string
  /** Keeps the header for screen readers while hiding it visually. */
  headerHidden?: boolean
  className?: string
  /**
   * A glyph before the header label, for a column that belongs to a channel.
   * Decorative: the header still carries the words.
   */
  headerIcon?: ReactNode
  /**
   * What the column holds, in a sentence, shown under its name in the columns
   * picker so somebody choosing columns knows what each one is before turning
   * it on.
   */
  description?: string
  /**
   * A second line under the header, in sentence case.
   *
   * This is where a limit belongs. "LinkedIn actions today" with "max 30 each"
   * under it tells somebody what they are up against while they are reading the
   * column, rather than after something has been refused.
   */
  headerNote?: ReactNode
  /**
   * Keeps the column out of the picker and always on screen. The first column
   * is fixed whether or not it says so, and a column of row actions should be.
   */
  fixed?: boolean
  /** Makes the header a sort button. Needs `sort` and `onSortChange` on the table. */
  sortable?: boolean
  /**
   * `add` is the "+ Add column" header cell: never offered in the picker and
   * never sortable. Its header is usually an `AddColumnButton`.
   */
  kind?: 'data' | 'add'
  /** Overrides the cell tone. The first column is `primary`, the rest `secondary`. */
  tone?: 'primary' | 'secondary'
}

/**
 * Which columns are drawn, given the ones a reader has turned off.
 *
 * The first column and anything `fixed` are never dropped, whatever the hidden
 * set says. That is not defensive tidiness: the hidden set can outlive the
 * column list it was built against, through a saved preference or a column that
 * became fixed after somebody hid it, and a table with no first column has no
 * link into a row.
 */
export function visibleColumns<T>(columns: readonly Column<T>[], hidden: readonly string[]): Column<T>[] {
  if (hidden.length === 0) return [...columns]
  const off = new Set(hidden)
  return columns.filter(
    (column, index) => index === 0 || column.fixed === true || column.kind === 'add' || !off.has(column.key),
  )
}

/** The columns the picker lists. Everything the reader is allowed to turn off. */
/** The rows the columns button offers: each pickable column by its header, with its description. */
export function columnChoices<T>(columns: readonly Column<T>[]): ColumnChoice[] {
  return pickableColumns(columns).map((column) => ({
    key: column.key,
    label: column.header,
    ...(column.description === undefined ? {} : { description: column.description }),
  }))
}

export function pickableColumns<T>(columns: readonly Column<T>[]): Column<T>[] {
  return columns.filter((column, index) => index > 0 && column.fixed !== true && column.kind !== 'add')
}

// ---------------------------------------------------------------------------
// The toolbar and the footer
// ---------------------------------------------------------------------------

export interface TableToolbarProps {
  /** Plain text, the way the design writes it: "12 sequences". `countLabel` builds one. */
  count?: ReactNode
  /**
   * A quieter line after the count, for how much of it is on screen or what
   * the list is doing: `Showing 50 of 212`, `Refreshing`. 14/20 in the muted
   * ink, and announced politely when it changes.
   */
  status?: ReactNode
  /** The search field. A `SearchInput` in every case so far. 250 wide, as Salesforge's. */
  search?: ReactNode
  /** The `FilterPopover`, after the search. */
  filters?: ReactNode
  /** The `SortPopover`, after the filters. */
  sort?: ReactNode
  /** Controls at the far right. `DataTable` adds the columns button after these. */
  actions?: ReactNode
  /**
   * While rows are picked, the count, search, filters, sort and actions step
   * aside for `Selected:`, the number, and the bulk actions.
   */
  selection?: SelectionBarProps
  /** Stays at the far right in both modes. `DataTable` puts the columns button here. */
  trailing?: ReactNode
  className?: string
}

/**
 * The row above the rows, inside the card rather than above it.
 *
 * Inside is the part that matters. A search box floating above a card reads as
 * a page control and leaves the card looking like a result somebody else
 * produced. Sitting on the card's own top edge, it reads as belonging to this
 * table, which is what it filters.
 */
export function TableToolbar({ count, status, search, filters, sort, actions, selection, trailing, className }: TableToolbarProps) {
  const selecting = selection !== undefined && selection.count > 0
  return (
    <div className={cn('flex flex-wrap items-center gap-3 border-b border-line px-4 py-3', className)}>
      {selecting ? (
        <SelectionBar {...selection} />
      ) : (
        <>
          {count === undefined ? null : <p className="shrink-0 text-base font-semibold text-ink">{count}</p>}
          {status === undefined || status === null ? null : (
            <p aria-live="polite" data-table-status="" className="shrink-0 text-base text-ink-muted">
              {status}
            </p>
          )}
          {/*
           * `min-w-0` is what lets the field shrink at all. A flex child's minimum
           * is its content width by default, so without it the search box refuses
           * to give ground and pushes the columns button off the end.
           */}
          {search === undefined ? null : <div className="w-full min-w-0 sm:w-[250px]">{search}</div>}
          {filters ?? null}
          {sort ?? null}
          {actions === undefined ? null : <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
        </>
      )}
      {trailing === undefined ? null : (
        <div className={cn('flex items-center gap-2', (selecting || actions === undefined) && 'ml-auto')}>{trailing}</div>
      )}
    </div>
  )
}

export interface TableFooterProps extends PaginationProps {
  /**
   * Shows a `Total items: n` chip on the left instead of the rows-per-page
   * picker, the way the builder's contacts table does.
   */
  showTotal?: boolean
}

/**
 * The footer the design asks for: rows-per-page on the left, the page and its
 * two arrows on the right.
 *
 * IT TAKES `PaginationProps` ON PURPOSE. `Pagination` already exists, already
 * has the cursor arithmetic, and `useListPage().bind()` already produces this
 * exact object, so this is a second face on one model rather than a second
 * model. Building a pager of its own here would have meant two places that
 * count pages and one of them going wrong quietly, which is the history
 * `Pagination`'s own header warns about.
 *
 * The two differ only in what they say. `Pagination` writes the row range,
 * which is the better answer under a card on a page of several lists.
 * This one writes the page number, which is the better answer on the bottom
 * edge of the table it belongs to.
 */
export function TableFooter({
  pageIndex,
  pageSize,
  total,
  canGoBack,
  hasNext,
  onBack,
  onNext,
  onPageSizeChange,
  pageSizes = [...PAGE_SIZES],
  isLoading,
  className,
  showTotal = false,
}: TableFooterProps) {
  let left: ReactNode = <span />
  if (showTotal && total !== undefined) {
    left = (
      <Badge tone="neutral" size="sm">
        <span className="tabular-nums">{totalItemsLabel(total)}</span>
      </Badge>
    )
  } else if (onPageSizeChange !== undefined) {
    left = (
      <label className="flex items-center gap-1.5">
        <span className="sr-only">Rows per page</span>
        <Select
          className="w-[7.5rem]"
          value={String(pageSize)}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          options={pageSizeOptions(pageSize, pageSizes).map((size) => ({
            value: String(size),
            label: `${String(size)} rows`,
          }))}
        />
      </label>
    )
  }

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 px-6 py-3', className)}>
      {left}
      <div className="flex items-center gap-3">
        <p aria-live="polite" className="text-base font-medium tabular-nums text-ink-secondary">
          {pageLabel(pageIndex, pageSize, total)}
        </p>
        <IconButton
          label="Previous page"
          variant="secondary"
          size="sm"
          onClick={onBack}
          disabled={!canGoBack || isLoading === true}
        >
          <IconChevronLeft className="h-5 w-5" />
        </IconButton>
        <IconButton
          label="Next page"
          variant="secondary"
          size="sm"
          onClick={onNext}
          disabled={!hasNext || isLoading === true}
        >
          <IconChevronRight className="h-5 w-5" />
        </IconButton>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Row parts
// ---------------------------------------------------------------------------

/**
 * Whether a click inside a row landed on something that does its own thing.
 *
 * The row forwards a click to its link, and a click on the checkbox, the kebab
 * or any other control in the row must not open the row as well.
 */
function landedOnControl(target: EventTarget, row: Element): boolean {
  // A click that did not land in the row's own DOM came through a portal: an
  // item in the row's menu, or a dialog opened from the row. React still
  // bubbles it up to the row, and it is never a click on the row. Without this
  // every kebab item on a table with a row click also opened the row.
  if (typeof Node !== 'undefined' && target instanceof Node && !row.contains(target)) return true
  if (!(target instanceof Element)) return false
  const control = target.closest('a, button, input, select, textarea, label, [role="menuitem"], [role="checkbox"]')
  return control !== null && row.contains(control)
}

/** The row's link, which the first cell renders and the row forwards clicks to. */
function rowAnchor(row: Element): HTMLAnchorElement | null {
  return row.querySelector<HTMLAnchorElement>('a[data-row-link]')
}

/**
 * Where a header being carried would land: a line in the accent on the side
 * of the header the pointer is on, the same 3px line a resize handle draws.
 */
export function ColumnDropLine({ side }: { side: 'before' | 'after' }) {
  return (
    <span
      aria-hidden="true"
      data-drop-side={side}
      className={cn('pointer-events-none absolute inset-y-0 w-[3px] bg-accent', side === 'before' ? 'left-0' : 'right-0')}
    />
  )
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

export interface TableSelection<T> {
  /** Ids from `getRowId`, across every page. */
  selected: readonly string[]
  onChange: (next: string[]) => void
  /** The bulk actions shown in the selection bar. */
  actions?: ReactNode
  /** The accessible name of a row's checkbox: "Select Agnes". Defaults to the row id. */
  rowLabel?: (row: T) => string
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[] | undefined
  getRowId: (row: T, index: number) => string
  /** Screen-reader description of what the table contains. */
  caption: string
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  /** What to show when the query succeeded and there is nothing to list. */
  empty: EmptyStateProps
  /**
   * The path a row opens. The first cell renders it as a real link, and a
   * click anywhere else on the row follows it, modifier keys included, so
   * Cmd-click and middle-click open a new tab.
   */
  rowLink?: (row: T) => string
  onRowClick?: (row: T) => void
  isRowSelected?: (row: T) => boolean
  dense?: boolean
  maxHeight?: string
  className?: string
  /** Rendered under the table, typically Pagination or TableFooter. */
  footer?: ReactNode
  skeletonRows?: number
  /**
   * The toolbar inside the card. Passing it moves the whole table into a card
   * of its own, with the toolbar on the top edge and the footer on the bottom.
   */
  toolbar?: TableToolbarProps
  /**
   * Pins the first column while the rest scroll under it, and lets the table
   * scroll sideways inside its card.
   *
   * It adds `group` to every row so the pinned cell can follow the row's hover,
   * so a cell that renders its own `group-hover` will start answering to the
   * whole row as well.
   */
  freezeFirstColumn?: boolean
  /**
   * `fit` keeps the table inside its card and breaks long values. `x` lets it
   * scroll sideways. Defaults to `x` with a frozen first column, `fit` without.
   */
  scroll?: 'fit' | 'x'
  /** Adds the columns button, and with it the card shell the toolbar brings. */
  columnPicker?: boolean
  /** The hidden keys, for a page that wants to remember them. Uncontrolled otherwise. */
  hiddenColumns?: readonly string[]
  /**
   * Columns that start behind the picker until this browser saves a choice of
   * its own, so a wide table opens with its row menu in view.
   */
  defaultHiddenColumns?: readonly string[]
  onHiddenColumnsChange?: (next: string[]) => void
  /**
   * Remembers the hidden columns in this browser under this key, when the page
   * does not control them itself. A per-viewer preference, so not in the URL.
   */
  tableKey?: string
  /** Checkboxes in the first column, a select-all in its header, and the selection bar. */
  selection?: TableSelection<T>
  /** The current sort, from `?sort=` and `?order=`. */
  sort?: TableSort
  onSortChange?: (next: TableSort) => void
  /**
   * A fill for the whole row, for a row that must read differently before
   * anybody reads a cell. The pinned first cell follows it. See `RowTone`.
   */
  rowTone?: (row: T) => RowTone | null | undefined
  /** Extra classes on a row's `<tr>`, after everything the table puts there. */
  rowClassName?: (row: T) => string | undefined
  /** Adds a last column holding a kebab menu per row. */
  rowActions?: (row: T) => readonly MenuSection[]
  /** The kebab's accessible name per row: "Actions for Spring launch". */
  rowActionsLabel?: (row: T) => string
  /**
   * A handle on every column's right edge, with the widths remembered per
   * table in this browser (under `tableKey`, else the caption). On unless a
   * page says otherwise.
   */
  resizableColumns?: boolean
  /**
   * Fills its parent's height instead of growing with its rows: the rows
   * scroll inside the card under a header that stays put, and the toolbar and
   * footer keep their places. The parent needs a height of its own, a flex
   * column with `min-h-0` in practice. Off by default.
   */
  fill?: boolean
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  caption,
  isLoading,
  error,
  onRetry,
  empty,
  rowLink,
  onRowClick,
  isRowSelected,
  dense,
  maxHeight,
  className,
  footer,
  skeletonRows = 6,
  toolbar,
  freezeFirstColumn = false,
  scroll,
  columnPicker = false,
  hiddenColumns,
  defaultHiddenColumns,
  onHiddenColumnsChange,
  tableKey,
  selection,
  sort,
  onSortChange,
  rowTone,
  rowClassName,
  rowActions,
  rowActionsLabel,
  resizableColumns = true,
  fill = false,
}: DataTableProps<T>) {
  /*
   * Every hook runs before the error branch below. An early return above a
   * `useState` is a hook that runs on some renders and not others, and the
   * error prop is exactly the sort of thing that arrives on the second render.
   */
  const [ownHidden, setOwnHidden] = useState<readonly string[]>(() =>
    tableKey === undefined
      ? [...(defaultHiddenColumns ?? [])]
      : readHiddenColumnsOr(browserStore(), tableKey, defaultHiddenColumns ?? []),
  )
  const hidden = hiddenColumns ?? ownHidden

  const setHidden = useCallback(
    (next: string[]) => {
      if (hiddenColumns === undefined) {
        setOwnHidden(next)
        if (tableKey !== undefined) writeHiddenColumns(browserStore(), tableKey, next)
      }
      onHiddenColumnsChange?.(next)
    },
    [hiddenColumns, onHiddenColumnsChange, tableKey],
  )

  // The order a person dragged the columns into, then the ones they hid taken
  // out, so the picker lists every column in the order the table draws them.
  const memoryKey = tableWidthsKey(tableKey, caption)
  const order = useColumnOrder(memoryKey, columns)
  const shown = useMemo(() => visibleColumns(order.ordered, hidden), [order.ordered, hidden])
  const choices = useMemo(() => columnChoices(order.ordered), [order.ordered])
  const scrollX = (scroll ?? (freezeFirstColumn ? 'x' : 'fit')) === 'x'
  const widths = useColumnWidths(memoryKey, shown.map((column) => column.key), !scrollX)
  const resetLayout = useCallback(() => {
    widths.reset()
    order.reset()
  }, [widths, order])

  if (error) {
    return <ErrorState error={error} onRetry={onRetry} className={className} />
  }

  const showSkeleton = isLoading && !rows
  const isEmpty = !isLoading && rows !== undefined && rows.length === 0
  const visibleIds = rows?.map((row, index) => getRowId(row, index)) ?? []
  const check = selection === undefined ? 'none' : headerCheckState(visibleIds, selection.selected)
  const columnCount = shown.length + (rowActions === undefined ? 0 : 1)
  // The card shell is what puts the toolbar and the footer on the table's own
  // edges, and it is also what moves the horizontal scrollbar up under the rows
  // instead of leaving it at the bottom of the card.
  const shelled = toolbar !== undefined || columnPicker || selection !== undefined

  const headerLabel = (column: Column<T>): ReactNode => {
    const words =
      column.headerIcon === undefined && column.headerNote === undefined ? (
        <span>{column.header}</span>
      ) : (
        <span className="block">
          <span className={cn('flex items-center gap-1.5', column.numeric && 'justify-end')}>
            {column.headerIcon}
            <span>{column.header}</span>
          </span>
          {column.headerNote === undefined ? null : (
            // Normal case and normal tracking: the header is a label, a limit
            // is a sentence.
            <span className="mt-0.5 block font-normal normal-case tracking-normal">{column.headerNote}</span>
          )}
        </span>
      )
    if (column.headerHidden) return <span className="sr-only">{column.header}</span>
    if (column.sortable !== true || column.kind === 'add' || onSortChange === undefined) return words
    const on = sort !== undefined && sort.key === column.key
    const Arrow = sort === undefined || !on ? IconSwitchVertical : sort.order === 'asc' ? IconArrowUp : IconArrowDown
    return (
      <button
        type="button"
        onClick={() => onSortChange(nextHeaderSort(sort, column.key))}
        className={cn(
          'inline-flex items-center gap-1 rounded hover:text-ink-secondary focus-visible:outline-none focus-visible:shadow-focus',
          on && 'text-ink-secondary',
        )}
      >
        {words}
        <Arrow className={cn('h-3 w-3', on ? 'text-ink-subtle' : 'text-ink-faint')} />
      </button>
    )
  }

  const grid = (
    <Table
      dense={dense}
      caption={caption}
      className={cn(scrollX && 'w-max min-w-full', resizableColumns && widths.tableClassName)}
      style={resizableColumns ? widths.tableStyle : undefined}
    >
      <THead>
        <tr ref={resizableColumns ? widths.headRef : undefined}>
          {shown.map((column, columnIndex) => {
            const pinned = freezeFirstColumn && columnIndex === 0
            const label = headerLabel(column)
            const words =
              columnIndex === 0 && selection !== undefined ? (
                <span className="flex items-center gap-3">
                  <Checkbox
                    aria-label="Select all rows on this page"
                    className="mt-0"
                    checked={check === 'all'}
                    indeterminate={check === 'some'}
                    disabled={visibleIds.length === 0}
                    onChange={() => selection.onChange(toggleAllVisible(visibleIds, selection.selected))}
                  />
                  {label}
                </span>
              ) : (
                label
              )
            // A dragged width wins over the column's own hint, and the hint
            // stays where nobody has dragged, which is how the table opened.
            const dragged = resizableColumns ? widths.columnStyle(column.key) : undefined
            const carry = order.headerDrag(column.key)
            const landing = order.mark?.key === column.key ? order.mark.side : null
            return (
              <TH
                key={column.key}
                numeric={column.numeric}
                aria-sort={column.sortable === true && onSortChange !== undefined ? ariaSort(sort, column.key) : undefined}
                style={dragged ?? (column.width ? ({ width: column.width } as CSSProperties) : undefined)}
                className={cn(
                  column.className,
                  // A movable header is positioned for its drop line even
                  // where it carries no resize handle.
                  pinned ? FROZEN_HEAD_CLASS : (resizableColumns || carry !== null) && RESIZABLE_HEAD_CLASS,
                  carry !== null && 'cursor-grab active:cursor-grabbing',
                  order.dragging === column.key && 'opacity-50',
                )}
                {...(carry ?? {})}
              >
                {resizableColumns ? (
                  <>
                    <span className={HEAD_LABEL_CLASS}>{words}</span>
                    <ColumnResizeHandle
                      widths={widths}
                      index={columnIndex}
                      label={typeof column.header === 'string' ? column.header : column.key}
                      onReset={resetLayout}
                    />
                  </>
                ) : (
                  words
                )}
                {landing === null ? null : <ColumnDropLine side={landing} />}
              </TH>
            )
          })}
          {rowActions === undefined ? null : (
            // Salesforge prints the word, so the column reads as a column.
            <TH className="w-[87px]">Actions</TH>
          )}
        </tr>
      </THead>
      <TBody>
        {showSkeleton
          ? Array.from({ length: skeletonRows }, (_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} className={cn(freezeFirstColumn && 'group')}>
                {Array.from({ length: columnCount }, (_, columnIndex) => (
                  <TD
                    key={shown[columnIndex]?.key ?? `actions-${columnIndex}`}
                    className={cn(freezeFirstColumn && columnIndex === 0 && frozenCellClass(false, false))}
                  >
                    <span className="skeleton block h-3.5 w-full max-w-[12rem]" />
                  </TD>
                ))}
              </tr>
            ))
          : null}

        {!showSkeleton && rows
          ? rows.map((row, index) => {
              const id = getRowId(row, index)
              const href = rowLink?.(row)
              const checked = selection?.selected.includes(id) ?? false
              const selected = (isRowSelected?.(row) ?? false) || checked
              const tone = rowTone?.(row) ?? undefined
              // A row answers to the pointer only when it opens something.
              const interactive = href !== undefined || onRowClick !== undefined
              const activate = interactive
                ? (event: MouseEvent<HTMLTableRowElement>) => {
                    if (landedOnControl(event.target, event.currentTarget)) return
                    if (onRowClick !== undefined) {
                      onRowClick(row)
                      return
                    }
                    rowAnchor(event.currentTarget)?.dispatchEvent(
                      new window.MouseEvent('click', {
                        bubbles: true,
                        cancelable: true,
                        ctrlKey: event.ctrlKey,
                        metaKey: event.metaKey,
                        shiftKey: event.shiftKey,
                        altKey: event.altKey,
                      }),
                    )
                  }
                : undefined
              const middle =
                href === undefined
                  ? undefined
                  : (event: MouseEvent<HTMLTableRowElement>) => {
                      if (event.button !== 1 || landedOnControl(event.target, event.currentTarget)) return
                      const anchor = rowAnchor(event.currentTarget)
                      if (anchor !== null) window.open(anchor.href, '_blank', 'noopener,noreferrer')
                    }
              return (
                <TR
                  key={id}
                  onClick={activate}
                  onAuxClick={middle}
                  selected={selected}
                  hoverable={interactive}
                  tone={tone}
                  className={cn(freezeFirstColumn && 'group', rowClassName?.(row))}
                >
                  {shown.map((column, columnIndex) => {
                    const content = column.cell(row, index)
                    const pinned = freezeFirstColumn && columnIndex === 0
                    const body =
                      columnIndex === 0 && href ? (
                        <Link
                          to={href}
                          data-row-link=""
                          className="rounded-sm font-medium text-ink hover:text-accent-text hover:underline focus-visible:outline-none focus-visible:shadow-focus"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {content}
                        </Link>
                      ) : (
                        content
                      )
                    return (
                      <TD
                        key={column.key}
                        numeric={column.numeric}
                        tone={column.tone ?? (columnIndex === 0 ? 'primary' : 'secondary')}
                        className={cn(column.className, pinned && frozenCellClass(selected, interactive, tone))}
                      >
                        {columnIndex === 0 && selection !== undefined ? (
                          <span className="flex items-center gap-3">
                            <Checkbox
                              aria-label={selection.rowLabel?.(row) ?? `Select ${id}`}
                              className="mt-0"
                              checked={checked}
                              onChange={() => selection.onChange(toggleSelected(selection.selected, id))}
                            />
                            <span className="min-w-0">{body}</span>
                          </span>
                        ) : (
                          body
                        )}
                      </TD>
                    )
                  })}
                  {rowActions === undefined ? null : (
                    <TD className="w-16 text-right">
                      <RowKebab label={rowActionsLabel?.(row) ?? 'Row actions'} sections={rowActions(row)} />
                    </TD>
                  )}
                </TR>
              )
            })
          : null}

        {isEmpty ? (
          <tr>
            <td colSpan={columnCount}>
              <div className="flex min-h-[320px] items-center justify-center">
                <EmptyState {...empty} />
              </div>
            </td>
          </tr>
        ) : null}
      </TBody>
    </Table>
  )

  // Nobody sees this line, which is exactly why it said "1 rows shown" for as
  // long as it has existed. A screen reader reads it out loud every time the
  // list settles.
  const live = (
    <span aria-live="polite" className="sr-only">
      {isLoading ? 'Loading rows' : rows ? `${countLabel(rows.length, 'row')} shown` : ''}
    </span>
  )

  if (!shelled) {
    return (
      <div className={cn('space-y-3', fill && 'flex min-h-0 flex-col', className)}>
        <TableContainer maxHeight={maxHeight} scrollX={scrollX} className={fill ? 'min-h-0 flex-1' : undefined}>
          {grid}
        </TableContainer>
        {footer}
        {live}
      </div>
    )
  }

  return (
    <div className={cn('overflow-hidden rounded-card border border-line bg-surface', fill && 'flex min-h-0 flex-col', className)}>
      <TableToolbar
        count={toolbar?.count}
        status={toolbar?.status}
        search={toolbar?.search}
        filters={toolbar?.filters}
        sort={toolbar?.sort}
        actions={toolbar?.actions}
        selection={
          selection === undefined
            ? toolbar?.selection
            : {
                count: selection.selected.length,
                onClear: () => selection.onChange([]),
                actions: selection.actions,
              }
        }
        trailing={
          columnPicker || toolbar?.trailing !== undefined ? (
            <>
              {toolbar?.trailing}
              {columnPicker ? (
                <ColumnPicker
                  choices={choices}
                  hidden={hidden}
                  onChange={setHidden}
                  onStep={order.step}
                  onReset={order.reordered || widths.resized ? resetLayout : undefined}
                />
              ) : null}
            </>
          ) : undefined
        }
        className={toolbar?.className}
      />
      <TableContainer flush maxHeight={maxHeight} scrollX={scrollX} className={fill ? 'min-h-0 flex-1' : undefined}>
        {grid}
      </TableContainer>
      {/*
        Truthiness and not `!== undefined`: the footer slot is conventionally
        filled with `condition ? <Pagination /> : null`, and a border drawn
        around that `null` is a stray line across the bottom of the card.
      */}
      {footer ? <div className="border-t border-line">{footer}</div> : null}
      {live}
    </div>
  )
}
